import {inject, Injectable} from '@angular/core';
import {Column, ColumnGroup, FlexGrid} from '@mescius/wijmo.grid';
import {Stdlib} from '../lib/stdlib';
import {FlexGridFilter} from '@mescius/wijmo.grid.filter';
import {SortDescription} from '@mescius/wijmo';
import {StringUtils, trimIndent} from '../lib/strings';
import {Alerts} from '../lib/alerts';
import {KeyValueAndParamsService} from './key-value-and-params.service';
import {AuthService} from '../security/auth.service';
import {AppConstants} from '../app.constants';
import {AccountGridService, WijmoColumnDeclaration} from '../views/account-grid/account-grid-helper';
import {PortfolioSegmentDataContext} from '../data/portfolio-segment-data-context.service';
import {AppLocalStorage} from '../lib/app-localstorage';

export interface GridLayoutState {
    columns: WijmoColumnDeclaration[];
    filterDefinition: string | undefined;
    sortDescriptions: { property: string, ascending: boolean }[];
    frozenColumns: number;
}

export interface StoredGridLayoutTreeItem {
    name: string;
    isFolder: boolean;
    reference?: string;
    children: StoredGridLayoutTreeItem[];
}

@Injectable({
    providedIn: 'root'
})
export class AppGridViewService {

    private readonly pocketBaseService = inject(KeyValueAndParamsService);
    private readonly authService = inject(AuthService);

    constructor() { }

    private static dumpGridColumnLayout(flexGrid: FlexGrid, column: Column | ColumnGroup): WijmoColumnDeclaration {
        const isColumnGroup = (column instanceof ColumnGroup) && !column.binding;
        const columns = isColumnGroup ? column.columns : [];
        if (isColumnGroup) {
            return Stdlib.deleteAllUndefinedOrNull({
                header: column.header,
                name: column.name,
                align: column.align,
                collapseTo: (column as ColumnGroup).collapseTo,
                isCollapsed: (column as ColumnGroup).isCollapsed,
                attribute: column.binding,
                visible: column.visible,
                columns: columns?.map(col => AppGridViewService.dumpGridColumnLayout(flexGrid, col))
            });
        } else {
            return Stdlib.deleteAllUndefinedOrNull({
                attribute: column.binding,
                visible: column.visible
            });
        }
    }

    static takeColumnLayoutSnapshot(flexGrid: FlexGrid): WijmoColumnDeclaration[] {
        return flexGrid.getColumnGroups().map(col => AppGridViewService.dumpGridColumnLayout(flexGrid, col));
    }

    static takeLayoutSnapshot(flexGrid: FlexGrid, filter: FlexGridFilter | undefined, withSorting: boolean | undefined): GridLayoutState {
        const columns = AppGridViewService.takeColumnLayoutSnapshot(flexGrid);
        const sortingMap = flexGrid.collectionView.sortDescriptions.map(function (sortDesc) {
            return { property: sortDesc.property, ascending: sortDesc.ascending };
        });
        const sortDescriptions = withSorting ? sortingMap : [];
        const filterDefinition = filter?.filterDefinition;
        const frozenColumns = flexGrid.frozenColumns;
        return { columns, filterDefinition, sortDescriptions, frozenColumns };
    }

    static async restoreLayout(flexGrid: FlexGrid, filter: FlexGridFilter | undefined, state: GridLayoutState, portfolioSegmentData?: PortfolioSegmentDataContext) {
        // restore column layout ... todo
        if (state.columns) {
            await AccountGridService.configureGridColumns(flexGrid, state.columns, portfolioSegmentData);
            flexGrid.frozenColumns = state.frozenColumns;
        }

        // restore sort state
        const view = flexGrid.itemsSource;
        if (!view || !view.sortDescriptions) {
            console.warn('No valid collection view found to restore sort descriptions.');
            return;
        }
        view.deferUpdate(function () {
            view.sortDescriptions.clear();
            for (let i = 0; i < state.sortDescriptions.length; i++) {
                const sortDesc = state.sortDescriptions[i];
                view.sortDescriptions.push(new SortDescription(sortDesc.property, sortDesc.ascending));
            }
        });

        // restore filter definitions
        if (filter?.filterDefinition != undefined) {
            setTimeout(() => filter.filterDefinition = state.filterDefinition!, 10);
        }
    }

    saveGridLayout(state: GridLayoutState): string {
        const key = AppConstants.STORAGE_PREFIX + StringUtils.randomString(10);
        AppLocalStorage.setJsonItem(key, state);
        return key;
    }

    loadGridLayout(key: string): GridLayoutState {
        const parsed = AppLocalStorage.getJsonItem(key) ?? {} as GridLayoutState;
        return {
            columns: parsed?.columns ?? [],
            filterDefinition: parsed?.filterDefinition ?? '',
            sortDescriptions: Array.isArray(parsed?.sortDescriptions) ? parsed.sortDescriptions : [],
            frozenColumns: parsed?.frozenColumns ?? 0
        };
    }

    loadGridLayoutTree(storageKey: string = AppConstants.STORAGE_TREE_KEY): StoredGridLayoutTreeItem {
       const json = localStorage.getItem(storageKey);
       return json ? JSON.parse(json) : {
           name: 'Favorites',
           isFolder: true,
           children: []
       }
    }

    saveGridLayoutTree(root: StoredGridLayoutTreeItem, storageKey: string = AppConstants.STORAGE_TREE_KEY): void {
        localStorage.setItem(storageKey, JSON.stringify(root));
    }

    static storeAccountColumnGroupState(groupName: string, state: boolean) {
        const key = AppConstants.STORAGE_PREFIX + 'account_column_group_state';
        AppLocalStorage.setJsonItem(`${key}.${groupName}`, state);
    }

    static getAccountColumnGroupState(groupName: string): boolean {
        const key = AppConstants.STORAGE_PREFIX + 'account_column_group_state';
        return AppLocalStorage.getJsonItem(`${key}.${groupName}`) ?? false;
    }

    addFolder(item: StoredGridLayoutTreeItem, name: string = 'New Folder'): StoredGridLayoutTreeItem {
        const newItem = {
            name: name,
            isFolder: true,
            children: []
        }
        item.children.push(newItem);
        return newItem;
    }

    addItem(item: StoredGridLayoutTreeItem, name: string, reference: string): StoredGridLayoutTreeItem {
        const newItem = {
            name: name,
            isFolder: false,
            reference: reference,
            children: []
        }
        item.children.push(newItem);
        return newItem;
    }

    private collectUsedRefs(root: StoredGridLayoutTreeItem, usedRefs: Set<string>): Set<string> {
        if (root.reference) {
            usedRefs.add(root.reference);
        }
        for (const child of root.children) {
            this.collectUsedRefs(child, usedRefs);
        }
        return usedRefs;
    }

    cleanupLocalStorageForTreeGridLayout(root: StoredGridLayoutTreeItem): void {
        const keysInUse = this.collectUsedRefs(root, new Set<string>());

        for (const key of Object.keys(localStorage)) {
            if (key.startsWith(AppConstants.STORAGE_PREFIX) && !keysInUse.has(key)) {
                localStorage.removeItem(key);
            }
        }
    }

    async showShareGridViewDialog(layout: GridLayoutState) {
        const reference = await this.pocketBaseService.createDataRecord(layout);
        const urlParams = new URLSearchParams(document.location.search);
        urlParams.delete('viewShareId');
        urlParams.set('viewShareId', reference);
        const url = document.URL.split('?')[0] + '?' + urlParams.toString();

        Alerts.alertShareLink({
            link: url,
            header: 'Share Link to View',
            title: trimIndent`
               A link to your current account view has been created.
               You can share it with anyone who has access to the portfolio data.
               Click here to copy the link to the clipboard:
            `,
        });
    }
}
